import {
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  AfterViewInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
  effect,
  input,
  computed,
  signal,
  NgZone,
  inject,
} from '@angular/core';

import { animate, state, style, transition, trigger } from '@angular/animations';
import SimpleKeyboard from 'simple-keyboard';
import type { default as KeyboardType } from 'simple-keyboard';

// Handle different module formats (ESM vs CommonJS) for production builds
const Keyboard: typeof KeyboardType = (SimpleKeyboard as unknown as { default: typeof KeyboardType }).default || SimpleKeyboard;
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

export interface VirtualKeyboardConfig {
  /** Show number row at top (default: true) */
  showNumbers?: boolean;
  /** Show space bar (default: true) */
  showSpace?: boolean;
  /** Show @ key - useful for email (default: true) */
  showAtSign?: boolean;
  /** Show . key (default: true) */
  showDot?: boolean;
  /** Show - key (default: false) */
  showDash?: boolean;
  /** Show _ key (default: false) */
  showUnderscore?: boolean;
  /** Custom label for enter key (default: 'OK') */
  enterLabel?: string;
  /** Show German umlauts ü, ö, ä, ß (default: true) */
  showUmlauts?: boolean;
  /** Show symbols button to access special characters (default: true) */
  showSymbols?: boolean;
}

const DEFAULT_CONFIG: VirtualKeyboardConfig = {
  showNumbers: true,
  showSpace: true,
  showAtSign: true,
  showDot: true,
  showDash: false,
  showUnderscore: false,
  enterLabel: 'OK',
  showUmlauts: true,
  showSymbols: true,
};

@Component({
  selector: 'sp-virtual-keyboard',
  standalone: true,
  imports: [MatIconModule, MatButtonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideUpDown', [
      state('hidden', style({
        transform: 'translateY(100%)',
        visibility: 'hidden',
      })),
      state('visible', style({
        transform: 'translateY(0)',
        visibility: 'visible',
      })),
      transition('hidden => visible', [
        style({ visibility: 'visible' }),
        animate('300ms ease-out'),
      ]),
      transition('visible => hidden', [
        animate('300ms ease-in'),
      ]),
    ]),
  ],
  template: `
    <div class="virtual-keyboard-container"
         [@slideUpDown]="animationState()"
         [style.left.px]="computedLeftOffset()">
      <div class="keyboard-header">
        <span class="keyboard-title">Tastatur</span>
        <button mat-icon-button (click)="onClose()" class="close-button" type="button">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <div #keyboardContainer class="simple-keyboard"></div>
    </div>
  `,
  styles: [`
    .virtual-keyboard-container {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background: var(--md-sys-color-surface-container-high);
      box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
      z-index: 1000;
      border-radius: 16px 16px 0 0;
      will-change: transform;
    }

    .keyboard-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 16px;
      background: var(--md-sys-color-surface-container-highest);
      border-radius: 16px 16px 0 0;
    }

    .keyboard-title {
      font-weight: 500;
      color: var(--md-sys-color-on-surface);
    }

    .close-button {
      color: var(--md-sys-color-on-surface-variant);
    }

    :host ::ng-deep .simple-keyboard {
      padding: 12px;
      background: var(--md-sys-color-surface-container-high);
    }

    :host ::ng-deep .simple-keyboard .hg-row {
      display: flex;
      justify-content: center;
    }

    :host ::ng-deep .simple-keyboard .hg-button {
      height: 50px;
      font-size: 18px;
      background: var(--md-sys-color-surface-container-lowest);
      color: var(--md-sys-color-on-surface);
      border: 1px solid var(--md-sys-color-outline-variant);
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      min-width: 35px;
    }

    :host ::ng-deep .simple-keyboard .hg-button:active {
      background: var(--md-sys-color-primary-container);
      color: var(--md-sys-color-on-primary-container);
    }

    :host ::ng-deep .simple-keyboard .hg-button.hg-functionBtn {
      background: var(--md-sys-color-surface-container);
      color: var(--md-sys-color-on-surface-variant);
    }

    :host ::ng-deep .simple-keyboard .hg-button.hg-button-enter {
      background: var(--md-sys-color-primary);
      color: var(--md-sys-color-on-primary);
    }

    :host ::ng-deep .simple-keyboard .hg-button.hg-button-enter:active {
      background: var(--md-sys-color-primary-container);
      color: var(--md-sys-color-on-primary-container);
    }

    :host ::ng-deep .simple-keyboard .hg-button.hg-button-space {
      min-width: 150px;
    }

    :host ::ng-deep .simple-keyboard .hg-button.hg-button-symbols,
    :host ::ng-deep .simple-keyboard .hg-button.hg-button-abc {
      min-width: 60px;
      font-size: 14px;
    }

    @media (max-width: 600px) {
      :host ::ng-deep .simple-keyboard .hg-button {
        height: 45px;
        font-size: 16px;
        min-width: 28px;
      }
      :host ::ng-deep .simple-keyboard .hg-button.hg-button-space {
        min-width: 100px;
      }
    }
  `],
})
export class VirtualKeyboardComponent implements AfterViewInit, OnDestroy {
  @ViewChild('keyboardContainer', { static: true }) keyboardContainer!: ElementRef;

  /** The current input value to sync with */
  inputValue = input<string>('');

  /** Whether the keyboard is visible */
  visible = input<boolean>(false);

  /** Keyboard configuration */
  config = input<VirtualKeyboardConfig>({});

  /** Reference to the currently focused input element for cursor position tracking */
  inputElement = input<HTMLInputElement | null>(null);

  /** Left offset in pixels (useful when sidebar is present). If not set, auto-detects from mat-sidenav-content */
  leftOffset = input<number | null>(null);

  /** Computed left offset - uses provided value or auto-detects from sidenav */
  protected computedLeftOffset = signal<number>(0);

  /** Emitted when a key is pressed and value changes */
  @Output() valueChange = new EventEmitter<string>();

  /** Emitted when Enter is pressed */
  @Output() enterPressed = new EventEmitter<void>();

  /** Emitted when close button is clicked */
  @Output() closeKeyboard = new EventEmitter<void>();

  /** Animation state based on visibility */
  protected animationState = computed(() => this.visible() ? 'visible' : 'hidden');

  private keyboard: KeyboardType | null = null;
  private currentValue = '';
  private currentConfig: VirtualKeyboardConfig = DEFAULT_CONFIG;
  private viewReady = signal(false);

  private readonly ngZone = inject(NgZone);

  constructor() {
    // Handle visibility changes - initialize or re-render keyboard
    effect(() => {
      const isVisible = this.visible();
      const isViewReady = this.viewReady();

      if (isVisible && isViewReady) {
        // Calculate left offset from sidenav if not explicitly provided
        this.updateLeftOffset();

        // Need to initialize or re-render
        this.ngZone.runOutsideAngular(() => {
          setTimeout(() => {
            if (!this.keyboard) {
              this.initKeyboard();
            } else {
              this.keyboard.render();
            }
          }, 50); // Small delay to ensure animation has started
        });
      }
    });

    // Sync input value changes to keyboard
    effect(() => {
      const value = this.inputValue();
      if (this.keyboard && value !== this.currentValue) {
        this.currentValue = value;
        this.keyboard.setInput(value);
      }
    });

    // Rebuild keyboard when config changes
    effect(() => {
      const newConfig = { ...DEFAULT_CONFIG, ...this.config() };
      if (this.keyboard && JSON.stringify(newConfig) !== JSON.stringify(this.currentConfig)) {
        this.currentConfig = newConfig;
        this.rebuildKeyboard();
      }
    });

    // Sync cursor position from input element
    effect(() => {
      const el = this.inputElement();
      if (el && this.keyboard) {
        const pos = el.selectionStart ?? el.value.length;
        this.keyboard.setCaretPosition(pos);
      }
    });
  }

  ngAfterViewInit(): void {
    this.currentConfig = { ...DEFAULT_CONFIG, ...this.config() };
    // Signal that view is ready
    this.viewReady.set(true);
  }

  ngOnDestroy(): void {
    this.keyboard?.destroy();
  }

  private buildLayout(): Record<string, string[]> {
    const cfg = this.currentConfig;

    // Build bottom row with optional keys
    const bottomRowKeys: string[] = [];
    if (cfg.showSymbols) bottomRowKeys.push('{symbols}');
    if (cfg.showAtSign) bottomRowKeys.push('@');
    if (cfg.showDot) bottomRowKeys.push('.');
    if (cfg.showDash) bottomRowKeys.push('-');
    if (cfg.showUnderscore) bottomRowKeys.push('_');
    if (cfg.showSpace) bottomRowKeys.push('{space}');
    bottomRowKeys.push('{enter}');

    const bottomRow = bottomRowKeys.join(' ');

    // Symbol bottom row (with ABC button to go back)
    const symbolBottomKeys: string[] = ['{abc}'];
    if (cfg.showSpace) symbolBottomKeys.push('{space}');
    symbolBottomKeys.push('{enter}');
    const symbolBottomRow = symbolBottomKeys.join(' ');

    // Build layouts
    const layouts: Record<string, string[]> = {};

    // Default layout (lowercase)
    const defaultLayout: string[] = [];
    if (cfg.showNumbers) {
      defaultLayout.push('1 2 3 4 5 6 7 8 9 0 {bksp}');
    }
    if (cfg.showUmlauts) {
      if (cfg.showNumbers) {
        defaultLayout.push('q w e r t z u i o p ü');
        defaultLayout.push('a s d f g h j k l ö ä');
        defaultLayout.push('{shift} y x c v b n m ß');
      } else {
        defaultLayout.push('q w e r t z u i o p ü {bksp}');
        defaultLayout.push('a s d f g h j k l ö ä');
        defaultLayout.push('{shift} y x c v b n m ß');
      }
    } else {
      if (cfg.showNumbers) {
        defaultLayout.push('q w e r t z u i o p');
        defaultLayout.push('a s d f g h j k l');
        defaultLayout.push('{shift} y x c v b n m');
      } else {
        defaultLayout.push('q w e r t z u i o p {bksp}');
        defaultLayout.push('a s d f g h j k l');
        defaultLayout.push('{shift} y x c v b n m');
      }
    }
    defaultLayout.push(bottomRow);
    layouts['default'] = defaultLayout;

    // Shift layout (uppercase) - German keyboard symbols on number row
    const shiftLayout: string[] = [];
    if (cfg.showNumbers) {
      // German keyboard: Shift + 1-0 = ! " § $ % & / ( ) =
      shiftLayout.push('! " § $ % & / ( ) = {bksp}');
    }
    if (cfg.showUmlauts) {
      if (cfg.showNumbers) {
        shiftLayout.push('Q W E R T Z U I O P Ü');
        shiftLayout.push('A S D F G H J K L Ö Ä');
        shiftLayout.push('{shift} Y X C V B N M ß');
      } else {
        shiftLayout.push('Q W E R T Z U I O P Ü {bksp}');
        shiftLayout.push('A S D F G H J K L Ö Ä');
        shiftLayout.push('{shift} Y X C V B N M ß');
      }
    } else {
      if (cfg.showNumbers) {
        shiftLayout.push('Q W E R T Z U I O P');
        shiftLayout.push('A S D F G H J K L');
        shiftLayout.push('{shift} Y X C V B N M');
      } else {
        shiftLayout.push('Q W E R T Z U I O P {bksp}');
        shiftLayout.push('A S D F G H J K L');
        shiftLayout.push('{shift} Y X C V B N M');
      }
    }
    shiftLayout.push(bottomRow);
    layouts['shift'] = shiftLayout;

    // Symbols layout (special characters)
    if (cfg.showSymbols) {
      layouts['symbols'] = [
        '! @ # $ % ^ & * ( ) {bksp}',
        '1 2 3 4 5 6 7 8 9 0',
        `- _ = + [ ] { } \\ |`,
        `; : ' " , . < > / ?`,
        '` ~ € £ ¥ © ® ™ ° •',
        symbolBottomRow,
      ];
    }

    return layouts;
  }

  private initKeyboard(): void {
    const container = this.keyboardContainer?.nativeElement;
    if (!container) {
      console.warn('[VirtualKeyboard] Container not ready, retrying...');
      setTimeout(() => this.initKeyboard(), 100);
      return;
    }

    // Destroy existing keyboard if any
    if (this.keyboard) {
      this.keyboard.destroy();
      this.keyboard = null;
    }

    // Clear container to ensure clean state
    container.innerHTML = '';

    const layout = this.buildLayout();
    const cfg = this.currentConfig;

    try {
      this.keyboard = new Keyboard(container, {
        onChange: (input: string) => this.ngZone.run(() => this.onChange(input)),
        onKeyPress: (button: string) => this.ngZone.run(() => this.onKeyPress(button)),
        layoutName: 'default',
        layout,
        display: {
          '{bksp}': '⌫',
          '{enter}': cfg.enterLabel || 'OK',
          '{space}': ' ',
          '{shift}': '⇧',
          '{symbols}': '!#1',
          '{abc}': 'ABC',
        },
        theme: 'hg-theme-default',
        mergeDisplay: true,
        useButtonTag: true,
      });

      // Set initial value
      const initialValue = this.inputValue();
      if (initialValue) {
        this.currentValue = initialValue;
        this.keyboard.setInput(initialValue);
      }
    } catch (error) {
      console.error('[VirtualKeyboard] Failed to initialize:', error);
    }
  }

  private rebuildKeyboard(): void {
    const currentInput = this.currentValue;
    if (this.keyboard) {
      this.keyboard.destroy();
      this.keyboard = null;
    }
    this.initKeyboard();
    // Re-check keyboard after init since it's set asynchronously
    setTimeout(() => {
      if (currentInput && this.keyboard) {
        this.keyboard.setInput(currentInput);
      }
    }, 0);
  }

  private onChange(input: string): void {
    this.currentValue = input;
    this.valueChange.emit(input);

    // Update cursor position in input element
    // Note: setSelectionRange throws on input types like "email" or "number"
    const el = this.inputElement();
    if (el && this.keyboard) {
      const caretPos = this.keyboard.getCaretPosition();
      setTimeout(() => {
        if (caretPos !== null) {
          try {
            el.setSelectionRange(caretPos, caretPos);
          } catch {
            // Ignore - some input types don't support setSelectionRange
          }
        }
      }, 0);
    }
  }

  private onKeyPress(button: string): void {
    if (!this.keyboard) return;

    if (button === '{shift}') {
      const currentLayout = this.keyboard.options.layoutName;
      this.keyboard.setOptions({
        layoutName: currentLayout === 'default' ? 'shift' : 'default',
      });
    } else if (button === '{symbols}') {
      this.keyboard.setOptions({
        layoutName: 'symbols',
      });
    } else if (button === '{abc}') {
      this.keyboard.setOptions({
        layoutName: 'default',
      });
    } else if (button === '{enter}') {
      this.enterPressed.emit();
    }
  }

  /** Call this method when the target input element changes */
  syncCaretPosition(element: HTMLInputElement): void {
    if (this.keyboard) {
      const pos = element.selectionStart ?? element.value.length;
      this.keyboard.setCaretPosition(pos);
    }
  }

  onClose(): void {
    this.closeKeyboard.emit();
  }

  /** Calculate the left offset based on sidenav position */
  private updateLeftOffset(): void {
    // If explicit offset is provided, use it
    const explicitOffset = this.leftOffset();
    if (explicitOffset !== null) {
      this.computedLeftOffset.set(explicitOffset);
      return;
    }

    // Auto-detect: find the mat-sidenav-content element and get its left position
    const sidenavContent = document.querySelector('mat-sidenav-content');
    if (sidenavContent) {
      const rect = sidenavContent.getBoundingClientRect();
      this.computedLeftOffset.set(rect.left);
    } else {
      this.computedLeftOffset.set(0);
    }
  }
}
